import { AnimatedList } from '../AnimatedList'
import { getUseBotOnboardingStore } from '../../stores/useBotOnboardingStore'
import { usePollIndexingFiles } from '../../hooks/usePollIndexingFiles'
import DocIcon from '~/assets/file-formats/doc-icon.svg?react'
import DocxIcon from '~/assets/file-formats/docx-icon.svg?react'
import HtmlIcon from '~/assets/file-formats/html-icon.svg?react'
import MdIcon from '~/assets/file-formats/md-icon.svg?react'
import PdfIcon from '~/assets/file-formats/pdf-icon.svg?react'
import TxtIcon from '~/assets/file-formats/txt-icon.svg?react'
import UrlIcon from '~/assets/file-formats/url-icon.svg?react'
import { Spinner } from '~/elements'
import { Check } from 'lucide-react'
import React, { ReactNode } from 'react'
import { cn } from '~/utils'
import { IndexedFile } from '../../stores/pollIndexingFilesSlice'

type AnimatedFilesListProps = {
  workspaceId: string
  botId: string
}

export const AnimatedFilesList = ({ workspaceId, botId }: AnimatedFilesListProps) => {
  const useBotOnboardingStore = getUseBotOnboardingStore({ botId, workspaceId })
  const isIndexing = useBotOnboardingStore((state) => state.isIndexing)
  const totalFileCount = useBotOnboardingStore((state) => state.totalFileCount)
  const showTraining = useBotOnboardingStore((state) => state.showTraining)
  const { indexedFiles } = usePollIndexingFiles({ workspaceId, botId })

  return (
    totalFileCount > 0 &&
    showTraining && (
      <>
        <div className="rounded-xl bg-neutral-50 p-4">
          {isIndexing ? (
            <>
              <div className="flex gap-2">
                <Spinner className="h-5 w-5" />
                <span className="text-sm font-medium text-neutral-950">training in progress...</span>
              </div>
              <AnimatedList
                items={
                  indexedFiles.length > 0
                    ? indexedFiles.map((f) => <IndexedFileRow key={f.name} file={f} />)
                    : getIndexedFilesPlaceholder()
                }
                numberOfVisibleItems={6}
                currentIndex={indexedFiles?.length ?? 0}
                currentElemPosition="bottom"
              />
            </>
          ) : (
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 rounded-full bg-green-200 !stroke-2 p-0.5 text-green-600" />
              <span className="text-sm text-neutral-950">Finished training</span>
            </div>
          )}
        </div>
      </>
    )
  )
}

const getIndexedFilesPlaceholder = (): ReactNode[] => {
  const LINES_WIDTHS = ['w-56', 'w-64', 'w-80', 'w-96', 'w-60', 'w-52']
  return LINES_WIDTHS.map((width, index) => (
    <div
      key={index}
      className={cn('h-5 animate-pulse rounded-full bg-neutral-200', width)}
      style={{ animationDelay: `${index * 0.3}s` }}
    />
  ))
}

const FILE_ICONS = {
  doc: DocIcon,
  docx: DocxIcon,
  html: HtmlIcon,
  md: MdIcon,
  pdf: PdfIcon,
  txt: TxtIcon,
  url: UrlIcon,
}

const getFileIcon = (filename: string) => {
  const extension = filename.slice(filename.lastIndexOf('.') + 1).toLowerCase()
  return FILE_ICONS[extension as keyof typeof FILE_ICONS] ?? FILE_ICONS.url
}

const IndexedFileRow = ({ file }: { file: IndexedFile }) => {
  const FileFormatIcon = getFileIcon(file.name)
  return (
    <div key={file.name} className="flex items-center gap-2">
      <FileFormatIcon className="h-4" />
      <span>
        finished learning on <span className=" text-neutral-950">{file.name}</span>
      </span>
    </div>
  )
}
